import { useState, useRef, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { List, Navigation, NavigationOff, Filter } from "lucide-react";
import UserMenu from "./components/UserMenu";
import OfflineIndicator from "./components/OfflineIndicator";
import UpdateNotification from "./components/UpdateNotification";
import AdminPage from "./pages/AdminPage";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TagsProvider } from "./contexts/TagsContext";
import { preloadShops } from "./services/shopService";
import { initializeDebugTools } from "./utils/debug";
import { userPreferences } from "./utils/userPreferences";
import { useTags } from "./contexts/TagsContext";

import AppHeader from "./components/AppHeader";
import FilterChips from "./components/FilterChips";
import MainContent from "./components/MainContent";
import MobileHeader from "./components/MobileHeader";
import Modals from "./components/Modals";
import NavPanel from "./components/NavPanel";
import ShopListContent from "./components/ShopListContent";
import { useMediaQuery } from "./hooks/useMediaQuery";




// Custom hooks
import { useReducedMotion } from "./hooks/useReducedMotion";
import { useLocation } from "./hooks/useLocation";
import { useTheme } from "./hooks/useTheme";
import { useFilters } from "./hooks/useFilters";
import { useShops, useMyShops } from "./hooks/useShops";

function AppContent() {
  const { shouldReduceMotion } = useReducedMotion();
  const { user } = useAuth();

  // Custom hooks for state management
  const { isDarkMode, toggleTheme } = useTheme();
  const { userLocation, isTrackingLocation, toggleLocationTracking } = useLocation();
  const { selectedFilters, toggleFilter } = useFilters();
  const { shops, loading, filteredShops, handleShopCreated, handleShopDeleted, handleShopUpdated } = useShops({
    selectedFilters,
  });

  // Modal state
  const [showListModal, setShowListModal] = useState(false);
  const [showMyShopsModal, setShowMyShopsModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  // Nav panel active tab (for desktop/tablet)
  const [activeNavTab, setActiveNavTab] = useState<'list' | 'filters' | null>(null);

  // Media query for responsive behavior
  const isTablet = useMediaQuery("(min-width: 768px)");

  // Settings state
  const [defaultMapType, setDefaultMapType] = useState<string>(userPreferences.defaultMapType || "roadmap");

  // Refs
  const closeUserMenuRef = useRef<(() => void) | null>(null);

  // Wrapper functions to ensure modal mutual exclusion
  const openListModal = () => {
    if (showListModal) {
      setShowListModal(false);
    } else {
      setShowMyShopsModal(false);
      setShowSettings(false);
      setShowListModal(true);
    }
  };

  const openMyShopsModal = () => {
    if (showMyShopsModal) {
      setShowMyShopsModal(false);
    } else {
      setShowListModal(false);
      setShowSettings(false);
      setShowMyShopsModal(true);
    }
  };

  const openSettingsModal = () => {
    if (showSettings) {
      setShowSettings(false);
    } else {
      setShowListModal(false);
      setShowMyShopsModal(false);
      setShowSettings(true);
    }
  };

  // Load tags from database
  const { tags } = useTags();

  // Map tags to filter format with icons
  const getTagIcon = (tagName: string) => {
    const iconMap: Record<string, string> = {
      'Genbrug': '🛍️',
      'Honey': '🐝',
      'Vegetables': '🥕',
      'Books': '📚',
      'Clothes': '👕',
      'Electronics': '📱',
      'Antiques': '🏺'
    };
    return iconMap[tagName] || '🏷️';
  };

  const filterTags = tags.map(tag => ({
    id: tag.name.toLowerCase(),
    icon: getTagIcon(tag.name),
    title: tag.name
  }));

  // Get user's shops
  const myShops = useMyShops(shops, user?.uid);

  // Initialize debug tools and preload shops on mount
  useMemo(() => {
    initializeDebugTools();
    preloadShops();
  }, []);

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
        <div className="text-center">
          <div className={`rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4 ${shouldReduceMotion ? '' : 'animate-spin'}`}></div>
          <p className={`${isDarkMode ? "text-gray-300" : "text-gray-600"} mb-4`}>
            {userLocation ? "Loading shops..." : "Finding shops near you..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"} relative main-app-container`}>
      <MainContent
        filteredShops={filteredShops}
        userLocation={userLocation}
        isDarkMode={isDarkMode}
        isTrackingLocation={isTrackingLocation}
        defaultMapType={defaultMapType}
        closeUserMenu={closeUserMenuRef.current}
        myShops={myShops}
        handleShopDeleted={handleShopDeleted}
        handleShopUpdated={handleShopUpdated}
        showListModal={showListModal}
        showMyShopsModal={showMyShopsModal}
        onCloseListModal={() => setShowListModal(false)}
        onCloseMyShopsModal={() => setShowMyShopsModal(false)}
        showFilters={showFilters}
        navPanelOpen={activeNavTab !== null}
      />

      {/* Filter status indicator */}
      {selectedFilters.length > 0 && (
        <div className="absolute top-20 right-4 z-[6] hidden md:block">
          <div
            className={`px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? "bg-gray-800/90 text-gray-300 border border-gray-700/50" : "bg-white/90 text-gray-600 border border-gray-200/50"
              } backdrop-blur-sm shadow-sm`}
          >
            {filteredShops.length} shop{filteredShops.length !== 1 ? "s" : ""} found
          </div>
        </div>
      )}

      {/* Desktop/Tablet: NavPanel - Google Maps style side navigation */}
      {isTablet && (
        <NavPanel
          isDarkMode={isDarkMode}
          activeTab={activeNavTab}
          onTabChange={(tab) => {
            setActiveNavTab(tab);
            // Sync with showListModal for MainContent
            if (tab === 'list') {
              setShowListModal(true);
              setShowFilters(false);
            } else if (tab === 'filters') {
              setShowFilters(true);
              setShowListModal(false);
            } else {
              setShowListModal(false);
              setShowFilters(false);
            }
          }}
          onOpenMyShops={openMyShopsModal}
          onOpenSettings={openSettingsModal}
          onMenuStateChange={(isOpen, closeFn) => {
            setIsUserMenuOpen(isOpen);
            closeUserMenuRef.current = closeFn || null;
          }}
          filterCount={selectedFilters.length}
        >
          {/* Content based on active tab */}
          {activeNavTab === 'list' && (
            <ShopListContent
              shops={filteredShops}
              userLocation={userLocation}
              isDarkMode={isDarkMode}
            />
          )}
          {activeNavTab === 'filters' && (
            <div className="p-4">
              <FilterChips
                filterTags={filterTags}
                selectedFilters={selectedFilters}
                isDarkMode={isDarkMode}
                onToggleFilter={toggleFilter}
                className="flex-wrap"
              />
            </div>
          )}
        </NavPanel>
      )}

      {/* Mobile only: Original AppHeader at top (hidden on tablet/desktop) */}
      <div className="md:hidden">
        <AppHeader isDarkMode={isDarkMode}>
          {/* Location Tracking Toggle */}
          <button
            onClick={toggleLocationTracking}
            className={`p-2 rounded-lg transition-all ${isTrackingLocation
              ? `${isDarkMode ? "text-green-400 hover:text-green-300 hover:bg-gray-700" : "text-green-600 hover:text-green-700 hover:bg-green-50"} location-tracking`
              : `${isDarkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"}`
              }`}
            title={isTrackingLocation ? "Stop tracking location" : "Start tracking location"}
          >
            {isTrackingLocation ? <Navigation className="h-4 w-4" /> : <NavigationOff className="h-4 w-4" />}
          </button>
          {/* List View Toggle */}
          <button
            onClick={openListModal}
            className={`p-2 rounded-lg ${showListModal ? `${isDarkMode ? "text-blue-400 hover:text-blue-300 hover:bg-gray-700" : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"}` : `${isDarkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"}`} transition-all`}
            title="Open List View"
          >
            <List className="h-4 w-4" />
          </button>
          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg transition-all ${showFilters
              ? `${isDarkMode ? "text-blue-400 hover:text-blue-300 hover:bg-gray-700" : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"}`
              : `${isDarkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"}`
              }`}
            title="Toggle filters"
          >
            <Filter className="h-4 w-4" />
          </button>
          {/* Divider */}
          <div className={`w-px h-6 ${isDarkMode ? " bg-gray-600" : "bg-gray-300"}`}></div>
          {/* User Menu */}
          <UserMenu
            isDarkMode={isDarkMode}
            onOpenMyShops={openMyShopsModal}
            onOpenSettings={openSettingsModal}
            onMenuStateChange={(isOpen, closeFn) => {
              setIsUserMenuOpen(isOpen);
              closeUserMenuRef.current = closeFn || null;
            }}
          />
        </AppHeader>
      </div>

      {/* Mobile Header - Bottom (extracted) */}
      <MobileHeader
        filterTags={filterTags}
        selectedFilters={selectedFilters}
        isDarkMode={isDarkMode}
        showFilters={showFilters}
        toggleFilter={toggleFilter}
        showListModal={showListModal}
        showMyShopsModal={showMyShopsModal}
        showSettings={showSettings}
        isTrackingLocation={isTrackingLocation}
        toggleLocationTracking={toggleLocationTracking}
        setShowFilters={setShowFilters}
        setShowListModal={openListModal}
        setShowMyShopsModal={openMyShopsModal}
        setShowSettings={setShowSettings}
        setIsUserMenuOpen={setIsUserMenuOpen}
      />

      {/* PWA Components */}
      <OfflineIndicator />
      <UpdateNotification />

      {/* Modals (AddCustomShop, SettingsPage) */}
      <Modals
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        isDarkMode={isDarkMode}
        toggleTheme={toggleTheme}
        isTrackingLocation={isTrackingLocation}
        toggleLocationTracking={toggleLocationTracking}
        defaultMapType={defaultMapType}
        setDefaultMapType={setDefaultMapType}
        userPreferences={userPreferences}
        userLocation={userLocation}
        onShopCreated={handleShopCreated}
        isUserMenuOpen={isUserMenuOpen}
        showFilters={showFilters}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <TagsProvider>
        <Router>
          <Routes>
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/*" element={<AppContent />} />
          </Routes>
        </Router>
      </TagsProvider>
    </AuthProvider>
  );
}

export default App;
